/**
 * @since 2026-10-05
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 *
 * Single entry point for calls to the backend business API. Every request carries
 * "Authorization: Bearer <Firebase id token>". The id token comes from the "token" cookie set at login (the
 * login goes through the backend, so the Firebase client SDK has no current user to ask). Id tokens expire after
 * one hour: on a 401 the token is renewed once with the "refresh_token" cookie through user/refresh-token/ and
 * the request is retried. The local session is cleared (and the browser sent to the login page) only when the
 * session is definitively invalid: the refresh is rejected (400/401 or no refresh token) or the retried request
 * is still 401. A 503 (authentication service unavailable) or a failed refresh caused by a network or server
 * error keeps the session and throws ServiceUnavailableError, so the caller can surface the error and retry.
 */

import { message } from "antd";
import Cookies from "universal-cookie";
import { BASE_BACKEND_URL } from "./constants";

const COOKIE_TOKEN = "token";
const COOKIE_REFRESH_TOKEN = "refresh_token";
const COOKIE_LIFETIME_MS = 86400000; // One day, as set at login
const LOGIN_PATH = "/login";
const UNAVAILABLE_NOTICE_INTERVAL_MS = 10000;

class UnauthorizedError extends Error {
  constructor() {
    super("Session expired, please log in again.");
    this.name = "UnauthorizedError";
  }
}

class ServiceUnavailableError extends Error {
  constructor(message) {
    super(message || "The service is temporarily unavailable, please retry.");
    this.name = "ServiceUnavailableError";
  }
}

// Outcomes of a refresh attempt
const REFRESH_REJECTED = "REJECTED";
const REFRESH_UNAVAILABLE = "UNAVAILABLE";

let refreshPromise = null;
let lastUnavailableNoticeMs = 0;

const clearSession = () => {
  const cookies = new Cookies();
  cookies.remove(COOKIE_TOKEN, { path: "/" });
  cookies.remove(COOKIE_REFRESH_TOKEN, { path: "/" });
  localStorage.clear();
};

const getIdToken = () => {
  return new Cookies().get(COOKIE_TOKEN);
};

const setSessionTokens = (idToken, refreshToken) => {
  const cookies = new Cookies();
  const options = {
    path: "/",
    expires: new Date(Date.now() + COOKIE_LIFETIME_MS),
  };
  cookies.set(COOKIE_TOKEN, idToken, options);
  if (refreshToken) {
    cookies.set(COOKIE_REFRESH_TOKEN, refreshToken, options);
  }
};

const redirectToLogin = () => {
  clearSession();
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
};

const refreshIdToken = async () => {
  // Concurrent 401s share one refresh call. Resolves to { idToken } or { failure: REFRESH_REJECTED | REFRESH_UNAVAILABLE }.
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = new Cookies().get(COOKIE_REFRESH_TOKEN);
      if (!refreshToken) {
        return { failure: REFRESH_REJECTED };
      }
      try {
        const response = await fetch(`${BASE_BACKEND_URL}/user/refresh-token/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (response.status === 400 || response.status === 401) {
          return { failure: REFRESH_REJECTED };
        }
        if (!response.ok) {
          return { failure: REFRESH_UNAVAILABLE };
        }
        const data = await response.json();
        if (!data || !data.access_token) {
          return { failure: REFRESH_UNAVAILABLE };
        }
        setSessionTokens(data.access_token, data.refresh_token);
        return { idToken: data.access_token };
      } catch (error) {
        // Network error: the refresh token was not judged, keep the session
        console.error(error);
        return { failure: REFRESH_UNAVAILABLE };
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const notifyUnavailable = (detail) => {
  // Callers swallow errors, so tell the user here; throttled because pages poll
  const now = Date.now();
  if (now - lastUnavailableNoticeMs < UNAVAILABLE_NOTICE_INTERVAL_MS) {
    return;
  }
  lastUnavailableNoticeMs = now;
  message.error(
    detail || "The service is temporarily unavailable, please retry."
  );
};

const readErrorDetail = async (response) => {
  try {
    const data = await response.json();
    return data && (data.detail || data.error);
  } catch (error) {
    return null;
  }
};

const throwIfUnavailable = async (response) => {
  if (response.status === 503) {
    const detail = await readErrorDetail(response);
    console.error(`Backend unavailable (503): ${detail || ""}`);
    notifyUnavailable(detail);
    throw new ServiceUnavailableError(detail);
  }
};

/**
 * fetch() for backend paths ("/strategy/..."), with the bearer token added. Do not set Content-Type for a
 * FormData body: the browser adds the multipart boundary.
 */
const authorizedFetch = async (path, options = {}) => {
  const send = (idToken) => {
    const headers = new Headers(options.headers || {});
    if (idToken) {
      headers.set("Authorization", `Bearer ${idToken}`);
    }
    return fetch(`${BASE_BACKEND_URL}${path}`, { ...options, headers });
  };
  let response = await send(getIdToken());
  await throwIfUnavailable(response);
  if (response.status !== 401) {
    return response;
  }
  const refresh = await refreshIdToken();
  if (refresh.failure === REFRESH_UNAVAILABLE) {
    notifyUnavailable(null);
    throw new ServiceUnavailableError();
  }
  if (refresh.idToken) {
    response = await send(refresh.idToken);
    await throwIfUnavailable(response);
    if (response.status !== 401) {
      return response;
    }
  }
  // Refresh rejected, or a fresh token is still refused: the session is over
  redirectToLogin();
  throw new UnauthorizedError();
};

export {
  authorizedFetch,
  clearSession,
  getIdToken,
  ServiceUnavailableError,
  setSessionTokens,
  UnauthorizedError,
};
